(() => {
  const escapeHTML = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const normalizeSizeLabel = (value = '') => {
    const label = String(value).trim();

    if (!/(pre[\s-]?order)/i.test(label)) return label;

    if (label.includes(':')) {
      return label.split(':').pop().trim();
    }

    return label
      .replace(/pre[\s-]?order/gi, '')
      .replace(/^\s*[-–—|]\s*/, '')
      .trim();
  };

  class RQCartUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.track = this.querySelector('[data-rq-upsell-track]');
      this.heading = this.querySelector('[data-rq-upsell-heading]');
      this.status = this.querySelector('[data-rq-upsell-status]');
      this.productId = this.dataset.productId || '';
      this.limit = Math.max(1, Number(this.dataset.limit || 4));
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'AUD';
      this.cartProductIds = new Set(
        String(this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      );

      this.addEventListener('click', (event) => this.handleClick(event));

      if (this.productId) {
        this.loadRecommendations();
      } else {
        this.syncVisibility();
      }
    }

    async loadRecommendations() {
      const baseURL = this.dataset.recommendationsUrl;
      if (!baseURL || !this.track) return;

      this.classList.add('is-loading');

      try {
        let products = await this.fetchRecommendations(baseURL, 'complementary');
        let heading = 'Complete the look';

        if (!products.length) {
          products = await this.fetchRecommendations(baseURL, 'related');
          heading = 'You may also like';
        }

        products = products
          .filter((product) => {
            const id = String(product.id || '');
            return product.available !== false && id && !this.cartProductIds.has(id);
          })
          .slice(0, this.limit);

        if (products.length) {
          this.track.innerHTML = products.map((product) => this.renderProduct(product)).join('');
          if (this.heading) this.heading.textContent = heading;
          this.hidden = false;
        }
      } catch (error) {
        console.warn('RQ cart recommendations could not be loaded.', error);
      } finally {
        this.classList.remove('is-loading');
        this.syncVisibility();
      }
    }

    async fetchRecommendations(baseURL, intent) {
      const endpoint = baseURL.endsWith('.json') ? baseURL : `${baseURL}.json`;
      const url = new URL(endpoint, window.location.origin);

      url.searchParams.set('product_id', this.productId);
      url.searchParams.set('limit', String(this.limit));
      url.searchParams.set('intent', intent);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Recommendations request failed with ${response.status}`);
      }

      const payload = await response.json();
      return Array.isArray(payload.products) ? payload.products : [];
    }

    handleClick(event) {
      const toggle = event.target.closest('[data-rq-upsell-toggle]');

      if (toggle && this.contains(toggle)) {
        event.preventDefault();
        this.toggleSizes(toggle);
        return;
      }

      const addButton = event.target.closest('[data-rq-upsell-add]');

      if (addButton && this.contains(addButton)) {
        event.preventDefault();
        this.addVariant(addButton);
      }
    }

    toggleSizes(toggle) {
      const card = toggle.closest('[data-rq-upsell-card]');
      const panel = card?.querySelector('[data-rq-upsell-sizes]');
      if (!panel) return;

      const shouldOpen = panel.hidden;

      this.querySelectorAll('[data-rq-upsell-sizes]').forEach((otherPanel) => {
        if (otherPanel !== panel) otherPanel.hidden = true;
      });

      this.querySelectorAll('[data-rq-upsell-toggle]').forEach((otherToggle) => {
        if (otherToggle !== toggle) otherToggle.setAttribute('aria-expanded', 'false');
      });

      panel.hidden = !shouldOpen;
      toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    }

    async addVariant(button) {
      const variantId = Number(button.dataset.rqUpsellAdd || 0);
      if (!variantId || button.disabled) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      this.setStatus('Adding item to cart.');

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sectionIds = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [
          'cart-drawer',
          'cart-icon-bubble',
        ];

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add this item.');
        }

        this.setStatus('Item added to cart.');

        if (cartDrawer && payload.sections) {
          cartDrawer.renderContents(payload);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        console.error(error);
        this.setStatus(error.message || 'Unable to add this item.', true);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    }

    renderProduct(product) {
      const title = String(product.title || '').split(' | ')[0].trim();
      const productURL = product.url || '#';
      const image = this.getProductImage(product);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const options = Array.isArray(product.options) ? product.options : [];
      const optionNames = options.map((option) =>
        typeof option === 'string' ? option : option?.name || '',
      );
      const sizeIndex = optionNames.findIndex((name) => String(name).trim().toLowerCase().includes('size'));
      const availableVariants = variants.filter((variant) => variant.available !== false);

      let actionMarkup = '';

      if (sizeIndex >= 0 && availableVariants.length) {
        const seen = new Set();
        const sizeButtons = availableVariants
          .map((variant) => {
            const rawLabel = variant.options?.[sizeIndex] || variant[`option${sizeIndex + 1}`] || '';
            const key = String(rawLabel);

            if (!key || seen.has(key)) return '';
            seen.add(key);

            const displayLabel = normalizeSizeLabel(key);

            return `
              <button
                type="button"
                class="rq-cart-upsell-card__size"
                data-rq-upsell-add="${escapeHTML(variant.id)}"
                aria-label="Add ${escapeHTML(title)}, size ${escapeHTML(displayLabel)}"
              >
                ${escapeHTML(displayLabel)}
              </button>
            `;
          })
          .join('');

        actionMarkup = `
          <button
            type="button"
            class="rq-cart-upsell-card__action"
            data-rq-upsell-toggle
            aria-expanded="false"
          >
            Quick add
          </button>
          <div class="rq-cart-upsell-card__sizes" data-rq-upsell-sizes hidden>
            ${sizeButtons}
          </div>
        `;
      } else if (availableVariants.length === 1 && variants.length === 1) {
        actionMarkup = `
          <button
            type="button"
            class="rq-cart-upsell-card__action"
            data-rq-upsell-add="${escapeHTML(availableVariants[0].id)}"
          >
            Add
          </button>
        `;
      } else {
        actionMarkup = `
          <a href="${escapeHTML(productURL)}" class="rq-cart-upsell-card__action">
            Choose options
          </a>
        `;
      }

      const comparePrice = Number(product.compare_at_price || product.compare_at_price_max || 0);
      const price = Number(product.price || product.price_min || 0);
      const compareMarkup = comparePrice > price
        ? `<s class="rq-cart-upsell-card__compare-price">${escapeHTML(this.formatMoney(comparePrice))}</s>`
        : '';

      return `
        <article
          class="rq-cart-upsell-card"
          data-rq-upsell-card
          data-product-id="${escapeHTML(product.id)}"
        >
          <a
            href="${escapeHTML(productURL)}"
            class="rq-cart-upsell-card__media"
            aria-label="${escapeHTML(title)}"
          >
            ${
              image
                ? `<img
                    class="rq-cart-upsell-card__image"
                    src="${escapeHTML(this.resizeImageURL(image, 360))}"
                    alt="${escapeHTML(title)}"
                    loading="lazy"
                    width="360"
                    height="504"
                  >`
                : '<span class="rq-cart-upsell-card__placeholder"></span>'
            }
          </a>

          <div class="rq-cart-upsell-card__content">
            <a href="${escapeHTML(productURL)}" class="rq-cart-upsell-card__title">
              ${escapeHTML(title)}
            </a>

            <div class="rq-cart-upsell-card__prices">
              ${compareMarkup}
              <span class="rq-cart-upsell-card__price">${escapeHTML(this.formatMoney(price))}</span>
            </div>

            <div class="rq-cart-upsell-card__actions">
              ${actionMarkup}
            </div>
          </div>
        </article>
      `;
    }

    getProductImage(product) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image?.url) return product.featured_image.url;
      if (Array.isArray(product.images) && product.images.length) {
        return typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url || '';
      }
      return '';
    }

    resizeImageURL(source, width) {
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (_) {
        return source;
      }
    }

    formatMoney(value) {
      const cents = Number(value || 0);

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      } catch (_) {
        return `${(cents / 100).toFixed(2)} ${this.currency}`;
      }
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = isError ? 'true' : 'false';
    }

    syncVisibility() {
      const hasCards = Boolean(this.track?.querySelector('[data-rq-upsell-card]'));
      this.hidden = !hasCards && !this.productId;
    }
  }

  if (!customElements.get('rq-cart-upsells')) {
    customElements.define('rq-cart-upsells', RQCartUpsells);
  }

  const syncCartDrawerEmptyState = (drawer) => {
    if (!drawer) return;

    const isEmpty = Boolean(drawer.querySelector('.drawer__inner-empty'));
    drawer.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
  };

  const enhanceCartDrawers = (scope = document) => {
    const drawers = scope.querySelectorAll?.('cart-drawer') || [];

    drawers.forEach((drawer) => {
      syncCartDrawerEmptyState(drawer);

      if (drawer.dataset.rqStateObserver === 'true') return;
      drawer.dataset.rqStateObserver = 'true';

      const observer = new MutationObserver(() => syncCartDrawerEmptyState(drawer));
      observer.observe(drawer, {
        childList: true,
        subtree: true,
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceCartDrawers());
  } else {
    enhanceCartDrawers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    enhanceCartDrawers(event.target);
  });
})();